<script lang="ts">
	import { enhance } from '$app/forms';
	import { page } from '$app/state';
	import Button from '$lib/buttons/v3/button.svelte';
	import Checkmark from '$lib/icons/checkmark.svelte';
	import { trackEvent } from '$lib/segment/client';
	import { event } from '$lib/utils/tracking';
	import type { ActionResult } from '@sveltejs/kit';
	import { fade, fly, slide } from 'svelte/transition';
	import type { HTMLAttributes } from 'svelte/elements';

	type FormState = 'processing' | 'success' | 'error';

	interface Props extends HTMLAttributes<HTMLInputElement> {
		label?: string;
		title?: string;
		onsuccess?: () => void;
		class?: string;
		name?: string;
	}

	let {
		label = 'Submit',
		title = 'Sign up for updates',
		onsuccess,
		class: className,
		name,
		...other
	}: Props = $props();

	let error = $state<string | null>(null);
	let formState = $state<FormState | null>(null);

	const handleEnhance = () => {
		formState = 'processing';

		return (response: { result: ActionResult }) => {
			event('Email Footer Submit Button Clicked');

			const { result } = response;

			if (result.type === 'failure') {
				error = result.data?.message || null;
				formState = 'error';
				return;
			}

			if (result.type !== 'success') {
				formState = 'error';
				return;
			}

			formState = 'success';
			onsuccess?.();

			trackEvent('Address email entered');
		};
	};

	let successfullyEntered = $derived(formState === 'success');
</script>

<form
	class={[className, 'relative flex flex-col']}
	method="POST"
	action="/?/newsletter"
	use:enhance={handleEnhance}
>
	<input type="hidden" name="pathname" value={page.url.pathname} />
	<div class="flex w-full flex-col">
		<div
			class="relative flex max-h-full flex-1 items-center justify-between rounded-[52px] bg-white"
		>
			<input
				{...other}
				{name}
				required
				type="email"
				inputmode="email"
				autocomplete="email"
				class="ml-6 mr-3 w-full flex-1 bg-transparent text-[16px] tracking-[-.16px] text-brown-1 outline-none transition-colors duration-500 placeholder:text-brown-2 lg:ml-8 lg:mr-4"
				class:text-stone-5={successfullyEntered}
				class:placeholder:text-stone-5={successfullyEntered}
			/>
			<div class="px-1 py-1 transition-all duration-500">
				<Button
					class="btn-brown {formState && ['processing', 'success'].includes(formState)
						? 'opacity-30'
						: ''}"
					type="submit"
					disabled={formState === 'processing'}
					{title}
				>
					{label}
				</Button>

				{#if successfullyEntered}
					<div
						class="absolute inset-1 flex select-none items-center justify-center gap-x-2 rounded-full bg-green-1 font-semibold text-white"
						in:fade={{ duration: 300 }}
					>
						<span in:fly={{ x: 30, duration: 400, delay: 100 }}>
							<Checkmark class="h-6 w-6" />
						</span>
						Success!
					</div>
				{/if}
			</div>
		</div>
		{#if formState === 'error' && error}
			<p class="px-6 py-2 text-red-400 lg:px-8" transition:slide>{error}</p>
		{/if}
	</div>
</form>

<style>
	input:-autofill,
	input:autofill:hover,
	input:autofill:focus,
	input:autofill:active {
		box-shadow: 0 0 0 30px white inset !important;
	}
</style>
